import React from "react";
import {
  Smartphone,
  MapPin,
  ShoppingCart,
  UtensilsCrossed,
} from "lucide-react";

interface StepProps {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  isLast?: boolean;
  side: "left" | "right";
}

const Step: React.FC<StepProps> = ({
  number,
  title,
  description,
  icon,
  isLast = false,
  side,
}) => (
  <div className="relative flex items-center w-full">
    {/* Desktop: Left content */}
    <div className={`hidden md:block w-1/2 pr-6 ${side === "left" ? "" : "invisible"}`}>
      {side === "left" && (
        <div className="text-right">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-salbox-neutral-100 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-salbox-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
              <div className="flex flex-col items-start text-left flex-1">
                <span className="bg-salbox-primary text-white font-lufga-bold text-xs px-2 py-1 rounded-full mb-2">
                  Paso {number}
                </span>
                <h3 className="text-lg font-lufga-semibold text-salbox-neutral-800 mb-2">
                  {title}
                </h3>
                <p className="text-salbox-neutral-600 font-lufga text-sm leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Timeline center */}
    <div className="relative flex flex-col items-center md:flex-shrink-0">
      <div className="w-4 h-4 bg-salbox-secondary rounded-full border-4 border-white shadow-lg z-10"></div>
    </div>

    {/* Desktop: Right content */}
    <div className={`hidden md:block w-1/2 pl-6 ${side === "right" ? "" : "invisible"}`}>
      {side === "right" && (
        <div className="text-left">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-salbox-neutral-100 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-salbox-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
              <div className="flex flex-col items-start text-left flex-1">
                <span className="bg-salbox-primary text-white font-lufga-bold text-xs px-2 py-1 rounded-full mb-2">
                  Paso {number}
                </span>
                <h3 className="text-lg font-lufga-semibold text-salbox-neutral-800 mb-2">
                  {title}
                </h3>
                <p className="text-salbox-neutral-600 font-lufga text-sm leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Mobile: Single centered card */}
    <div className="md:hidden pl-6 w-full">
      <div className="text-left">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-salbox-neutral-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-salbox-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
            <div className="flex flex-col items-start text-left flex-1">
              <span className="bg-salbox-primary text-white font-lufga-bold text-xs px-2 py-1 rounded-full mb-2">
                Paso {number}
              </span>
              <h3 className="text-lg font-lufga-semibold text-salbox-neutral-800 mb-2">
                {title}
              </h3>
              <p className="text-salbox-neutral-600 font-lufga text-sm leading-relaxed">
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const HowItWorks: React.FC = () => {
  const steps = [
    {
      title: "Descarga la app",
      description:
        "Descarga Salbox desde App Store o Google Play y crea tu cuenta en segundos",
      icon: <Smartphone className="w-8 h-8 text-salbox-primary" />,
    },
    {
      title: "Selecciona tu punto de entrega",
      description:
        "Elige el punto de entrega más conveniente cerca de ti o en tu ubicación actual",
      icon: <MapPin className="w-8 h-8 text-salbox-primary" />,
    },
    {
      title: "Realiza tu pedido",
      description:
        "Explora nuestro menú, elige tus platillos favoritos y paga de forma segura",
      icon: <ShoppingCart className="w-8 h-8 text-salbox-primary" />,
    },
    {
      title: "Disfruta de tu comida recién hecha",
      description:
        "Recibe tu comida caliente y deliciosa sin costo de envío donde tú estés",
      icon: <UtensilsCrossed className="w-8 h-8 text-salbox-primary" />,
    },
  ];

  return (
    <section id="como-funciona" className="py-16 bg-white scroll-mt-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-lufga-semibold text-salbox-secondary mb-4 uppercase">
            Cómo funciona Salbox
          </h2>
          <p className="text-lg font-lufga text-salbox-neutral-600 max-w-2xl mx-auto">
            Descubre lo fácil que es disfrutar de comida deliciosa con Salbox.
            Solo cuatro pasos te separan de una experiencia gastronómica única.
          </p>
        </div>

        {/* Timeline - Vertical with center line */}
        <div className="max-w-6xl mx-auto relative">
          {/* Continuous vertical line */}
          <div className="absolute top-0 bottom-0 w-1 bg-salbox-secondary/30 left-2 md:left-1/2 md:transform md:-translate-x-1/2"></div>

          <div className="space-y-4">
            {steps.map((step, index) => (
              <Step
                key={index}
                number={index + 1}
                title={step.title}
                description={step.description}
                icon={step.icon}
                side={index % 2 === 0 ? "left" : "right"}
                isLast={index === steps.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
